import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { ens_normalize } from "@adraffy/ens-normalize";
import { getAddress, isAddress } from "ethers";
import { useTranslation } from "react-i18next";

import { resolveNameWithProviderTimeout } from "@/safe/providerRequest";
import { useSafe } from "@/safe/SafeContextValue";

const STORAGE_KEY = "6529-safe-app.watchedSafe";
const RECENTS_STORAGE_KEY = "6529-safe-app.recentSafes";
const RECENTS_LIMIT = 5;

export type WatchedSafeManualSelection = {
  input: string;
  address: string;
  label: string;
  source: "address" | "ens";
};

export type WatchedSafeStatus = "idle" | "resolving" | "ready" | "error";

export type WatchedSafeContextValue = {
  activeSafeAddress: string | undefined;
  activeSafeLabel: string | undefined;
  activeSource: "connected-safe" | "manual-address" | "manual-ens" | "none";
  clearWatchedSafe(): void;
  error: string | null;
  manualSelection: WatchedSafeManualSelection | null;
  // Most-recent-first list of Safes previously watched on this device.
  recentSelections: readonly WatchedSafeManualSelection[];
  selectRecent(selection: WatchedSafeManualSelection): void;
  status: WatchedSafeStatus;
  submitWatchTarget(input: string): Promise<void>;
};

const WatchedSafeContext = createContext<WatchedSafeContextValue | undefined>(undefined);

function readStoredSelection(): WatchedSafeManualSelection | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<WatchedSafeManualSelection>;
    if (
      typeof parsed.input !== "string" ||
      typeof parsed.address !== "string" ||
      typeof parsed.label !== "string" ||
      // An empty label would suppress the header Safe chip while the pill
      // says "Watching" — reject tampered storage before it splits the register.
      parsed.label.trim().length === 0 ||
      (parsed.source !== "address" && parsed.source !== "ens") ||
      !isAddress(parsed.address)
    ) {
      return null;
    }

    return {
      input: parsed.input,
      address: getAddress(parsed.address),
      label: parsed.label,
      source: parsed.source
    };
  } catch {
    return null;
  }
}

function writeStoredSelection(selection: WatchedSafeManualSelection | null): void {
  if (typeof window === "undefined") return;

  try {
    if (!selection) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
  } catch {
    // Watch mode should keep working in memory when browser storage is unavailable.
  }
}

function isValidStoredSelection(value: unknown): value is WatchedSafeManualSelection {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Partial<WatchedSafeManualSelection>;
  return (
    typeof candidate.input === "string" &&
    typeof candidate.address === "string" &&
    typeof candidate.label === "string" &&
    // An empty label would suppress the header Safe chip while the pill says
    // "Watching" — reject tampered storage before it splits the register.
    candidate.label.trim().length > 0 &&
    (candidate.source === "address" || candidate.source === "ens") &&
    isAddress(candidate.address)
  );
}

function readStoredRecents(): WatchedSafeManualSelection[] {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isValidStoredSelection)
      .slice(0, RECENTS_LIMIT)
      .map((entry) => ({ ...entry, address: getAddress(entry.address) }));
  } catch {
    return [];
  }
}

function writeStoredRecents(recents: readonly WatchedSafeManualSelection[]): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recents));
  } catch {
    // Recents are a convenience; losing them must never break watching.
  }
}

function promoteRecent(
  recents: readonly WatchedSafeManualSelection[],
  selection: WatchedSafeManualSelection
): WatchedSafeManualSelection[] {
  const next = [
    selection,
    ...recents.filter((entry) => entry.address.toLowerCase() !== selection.address.toLowerCase())
  ].slice(0, RECENTS_LIMIT);
  return next;
}

function normalizeWatchInput(value: string): string {
  return value.trim();
}

export function WatchedSafeProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const safe = useSafe();
  const [initialSelection] = useState<WatchedSafeManualSelection | null>(() =>
    readStoredSelection()
  );
  const [manualSelection, setManualSelection] = useState<WatchedSafeManualSelection | null>(
    initialSelection
  );
  const [recentSelections, setRecentSelections] = useState<WatchedSafeManualSelection[]>(() => {
    const stored = readStoredRecents();
    // The persisted active selection belongs in the history too.
    return initialSelection ? promoteRecent(stored, initialSelection) : stored;
  });
  const [status, setStatus] = useState<WatchedSafeStatus>(() =>
    initialSelection ? "ready" : "idle"
  );
  const [error, setError] = useState<string | null>(null);
  const submitSequenceRef = useRef(0);

  const rememberSelection = useCallback((selection: WatchedSafeManualSelection) => {
    setRecentSelections((current) => {
      const next = promoteRecent(current, selection);
      writeStoredRecents(next);
      return next;
    });
  }, []);

  const connectedSafeAddress = safe.mode === "IN_SAFE" ? safe.safe?.safeAddress : undefined;
  const activeSafeAddress = connectedSafeAddress ?? manualSelection?.address;
  const activeSafeLabel = connectedSafeAddress
    ? t("watchedSafe.label.connectedSafe")
    : manualSelection
      ? manualSelection.label
      : undefined;
  const activeSource = connectedSafeAddress
    ? "connected-safe"
    : manualSelection?.source === "address"
      ? "manual-address"
      : manualSelection?.source === "ens"
        ? "manual-ens"
        : "none";

  const clearWatchedSafe = useCallback(() => {
    submitSequenceRef.current += 1;
    setManualSelection(null);
    setStatus("idle");
    setError(null);
    writeStoredSelection(null);
  }, []);

  const submitWatchTarget = useCallback(
    async (rawInput: string) => {
      const sequence = submitSequenceRef.current + 1;
      submitSequenceRef.current = sequence;
      const input = normalizeWatchInput(rawInput);
      setError(null);

      if (!input) {
        clearWatchedSafe();
        return;
      }

      if (isAddress(input)) {
        const address = getAddress(input);
        const selection: WatchedSafeManualSelection = {
          input,
          address,
          label: address,
          source: "address"
        };
        setManualSelection(selection);
        setStatus("ready");
        writeStoredSelection(selection);
        rememberSelection(selection);
        return;
      }

      let normalizedName: string;
      try {
        normalizedName = ens_normalize(input);
      } catch {
        setStatus("error");
        setError(t("watchedSafe.error.invalidInput"));
        return;
      }

      if (!safe.provider) {
        setStatus("error");
        setError(t("watchedSafe.error.ensNeedsProvider"));
        return;
      }

      setStatus("resolving");
      let resolved: string | null;
      try {
        resolved = await resolveNameWithProviderTimeout(safe.provider, normalizedName);
      } catch {
        if (submitSequenceRef.current !== sequence) {
          return;
        }
        setStatus("error");
        setError(t("watchedSafe.error.ensLookupFailed"));
        return;
      }
      if (submitSequenceRef.current !== sequence) {
        return;
      }
      if (!resolved || !isAddress(String(resolved))) {
        setStatus("error");
        setError(t("watchedSafe.error.ensNotResolved"));
        return;
      }

      const address = getAddress(String(resolved));
      const selection: WatchedSafeManualSelection = {
        input,
        address,
        label: normalizedName,
        source: "ens"
      };
      setManualSelection(selection);
      setStatus("ready");
      writeStoredSelection(selection);
      rememberSelection(selection);
    },
    [clearWatchedSafe, rememberSelection, safe.provider, t]
  );

  // Re-watching a remembered Safe reuses its resolved address directly — the
  // same trust as the persisted active selection, with no fresh lookup.
  const selectRecent = useCallback(
    (selection: WatchedSafeManualSelection) => {
      submitSequenceRef.current += 1;
      setManualSelection(selection);
      setStatus("ready");
      setError(null);
      writeStoredSelection(selection);
      rememberSelection(selection);
    },
    [rememberSelection]
  );

  const value = useMemo<WatchedSafeContextValue>(
    () => ({
      activeSafeAddress,
      activeSafeLabel,
      activeSource,
      clearWatchedSafe,
      error,
      manualSelection,
      recentSelections,
      selectRecent,
      status,
      submitWatchTarget
    }),
    [
      activeSafeAddress,
      activeSafeLabel,
      activeSource,
      clearWatchedSafe,
      error,
      manualSelection,
      recentSelections,
      selectRecent,
      status,
      submitWatchTarget
    ]
  );

  return <WatchedSafeContext.Provider value={value}>{children}</WatchedSafeContext.Provider>;
}

export function useWatchedSafe(): WatchedSafeContextValue {
  const ctx = useContext(WatchedSafeContext);
  if (!ctx) throw new Error("useWatchedSafe must be used within WatchedSafeProvider");
  return ctx;
}
